<template>
  <div class="main-box">
    <div class="labels gray--text">
      <div class="label" v-for="dataset in _datasets" :key="dataset.label">
        <div class="color" :style="{ background: dataset.backgroundColor }"></div>
        <div>{{ dataset.label }}</div>
      </div>
    </div>
    <div class="graphYTD">
      <ChartDates
        :datasets="datasets"
        :eu="eu"
        :period="intervalPeriod"
        :mobile="$vuetify.breakpoint.smAndDown"
      />
    </div>
  </div>
</template>

<script>
import { validateObject, setFakeValues } from "@/models/Utils";
import ChartDates from "./Charts/ChartDates.vue";
import SkeletonGraph from "@/utils/SkeletonGraph/SkeletonGraph";
import { themeColor } from "@/main.ts";
import { conversorFactor } from "@/models/Goals";

export default {
  components: {
    ChartDates,
  },
  props: {
    instances: Object,
    colors: Array,
    period: String,
    eu: [Object, String],
    loading: Boolean,
  },
  data: () => ({
    skeleton: new SkeletonGraph(),
    datasets: [],
    baseMeasurementDataset: {
      type: "bar",
      borderColor: "#72C4D6",
      backgroundColor: "#72C4D6",
    },
    baseGoalsDataset: {
      type: "scatter",
      pointStyle: "line",
      radius: 4,
      hoverRadius: 4,
      borderWidth: 2,
      hoverBorderWidth: 2,
      fill: false,
    },
    intervalPeriod: "",
    toExport: [],
  }),
  watch: {
    instances: {
      immediate: true,
      handler(v) {
        if (validateObject(v, ["goals", "historical"])) {
          this.setDatasets();
          this.buildExport();
        }
      },
    },
    loading: {
      immediate: true,
      handler(v) {
        if (this.loading)
          this.datasets = this.datasets.length
            ? this.changeDatasetColor()
            : this.setLoadingDataset();
      },
    },
  },
  computed: {
    _datasets() {
      let datasets = [...this.datasets];
      if (datasets.length > 0) {
        return datasets.filter((dataset) => dataset.data && dataset.data.length);
      }
      return [];
    },
  },
  methods: {
    setDatasets() {
      const goalsDatasets = this.buildGoalsDatasets();
      const measurementsDatasets = this.buildMeasurementsDatasets();

      const datasets = goalsDatasets.concat(measurementsDatasets);
      this.intervalPeriod = this.period;
      this.datasets = setFakeValues(datasets);
    },
    getHistoricalEu(notLower) {
      const { historical } = this.instances;
      if (!historical.historicalMeasures || !historical.historicalMeasures.length) return;
      return notLower
        ? historical.historicalMeasures[0].eu
        : historical.historicalMeasures[0].eu.toLowerCase();
    },
    getGoalEu() {
      const { goals } = this.instances;
      if (!goals || !goals.length) return;
      return goals[0].eu.toLowerCase();
    },
    convertGoals(dataset) {
      let energyEu = this.getHistoricalEu();
      let goalEu = this.getGoalEu();
      dataset = dataset.map((e) => {
        e.y = (Number(e.y) * conversorFactor[goalEu][energyEu]).toFixed(2);
        return e;
      });
      return dataset;
    },
    buildGoalsDatasets() {
      const { goals } = this.instances;
      const datasets = [];
      let energyEu = this.getHistoricalEu(true);
      goals.forEach((goal, i) => {
        datasets.push({
          label: `${goal.name} (${energyEu})`,
          data: this.convertGoals(goal.dataset()),
          borderColor: this.colors[i],
          backgroundColor: this.colors[i],
          ...this.baseGoalsDataset,
        });
      });

      return datasets;
    },
    setLoadingDataset() {
      return [
        {
          data: this.skeleton.setInitialData("monthly"),
          yAxisID: "left",
          type: "bar",
          backgroundColor: themeColor("loading"),
          borderColor: themeColor("loading"),
          label: "MWh",
        },
      ];
    },
    buildMeasurementsDatasets() {
      const { historical } = this.instances;
      const data = historical.sum();
      const label = historical.label() || "";

      return {
        ...this.baseMeasurementDataset,
        label,
        data,
      };
    },
    changeDatasetColor() {
      {
        let datasets = [...this.datasets];
        datasets.forEach((dataset) => {
          dataset.backgroundColor = themeColor("loading");
          dataset.borderColor = themeColor("loading");
        });
        return datasets;
      }
    },
    buildExport() {
      this.toExport = [];
      this.datasets.forEach((dataset) => {
        if (!dataset.label.includes("*")) {
          dataset.data.forEach((e) => {
            this.toExport.push({ name: dataset.label, timestamp: e.x, value: e.y });
          });
        }
      });
      this.$emit("toExport", this.toExport);
    },
  },
};
</script>

<style lang="css" scoped>
.main-box {
  height: 100%;
}
.graphYTD {
  position: relative;
  max-height: 100%;
  height: fit-content;
}
.header {
  height: 30px;
}
.labels {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  height: 50px;
}
.label {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  font-size: 12px;
}
.label > .color {
  width: 12px;
  height: 12px;
  border-radius: 4px;
  margin: 0 5px;
}
</style>
